<template>
  <header class="flex items-center justify-between px-6 py-2 shadow-md mb-3" :class="$store.getters['auth/backgroundColor']">
    <div class="flex items-center">
      <!-- Back Arrow Icon, shown only on NewDonorRegistrationForm page -->
      <button v-if="showBackArrow" @click="goBack" class="mr-4 focus:outline-none">
        <svg class="w-6 h-6 text-custom-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
      <div class="relative">
        <h3 class="font-custom-font-wight leading-custom-title-line-height text-custom-title-size text-custom-blue">
          {{ pageTitle }}
        </h3>
      </div>
    </div>

    <div class="flex items-center">
      <!-- Toggle Background Button -->
      <button @click="toggleBackground" class="mx-4 text-gray-600 focus:outline-none" title="Toggle Theme">
        <!-- Sun icon for light mode (bg-white) -->
        <svg v-if="$store.getters['auth/backgroundColor'] === 'bg-white'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <!-- Moon icon for gray mode (bg-gray-100) -->
        <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      </button>
      <div class="relative mx-4">
        <button
          @click="toggleNotificationPanel"
          class="relative flex text-gray-600 focus:outline-none"
          title="Blood appeal notifications"
          aria-label="Open blood appeal notifications"
          aria-haspopup="true"
          :aria-expanded="notificationOpen"
        >
        <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <!-- Notification Badge -->
        <span
          v-if="unreadCount > 0"
          class="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-white bg-red-500 border border-white rounded-full translate-x-1/4 -translate-y-1/4">
          {{ unreadCount > 99 ? "99+" : unreadCount }}
        </span>
        </button>

        <div v-show="notificationOpen" @click="notificationOpen = false" class="fixed inset-0 z-10 w-full h-full"></div>

        <transition enter-active-class="transition duration-150 ease-out transform"
          enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150 ease-in transform" leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0">
          <div
            v-show="notificationOpen"
            class="absolute right-0 z-20 mt-2 w-[22rem] max-w-[90vw] rounded-md bg-white shadow-xl border border-gray-100"
          >
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h4 class="text-sm font-semibold text-gray-800">Blood Appeals</h4>
              <button
                v-if="unreadCount > 0"
                @click="markAllAsRead"
                class="text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none"
              >
                Mark all as read
              </button>
            </div>

            <div v-if="notificationsLoading" class="px-4 py-6 text-sm text-gray-500 text-center">
              Loading notifications...
            </div>
            <div v-else-if="notificationError" class="px-4 py-4 text-sm text-red-600">
              {{ notificationError }}
            </div>
            <div v-else-if="appealNotifications.length === 0" class="px-4 py-6 text-sm text-gray-500 text-center">
              No blood appeals found.
            </div>
            <ul v-else class="max-h-96 overflow-y-auto">
              <li v-for="appeal in appealNotifications" :key="appeal.id">
                <div
                  class="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  :class="appeal.isUnread ? 'bg-red-50' : ''"
                >
                  <button
                    @click="handleAppealClick(appeal)"
                    class="w-full text-left focus:outline-none"
                  >
                  <div class="flex items-start justify-between gap-3">
                    <div class="text-sm font-semibold text-gray-800">
                      {{ appeal.appealSerial }}
                    </div>
                    <span
                      v-if="appeal.isUnread"
                      class="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                    >
                      New
                    </span>
                  </div>
                  <p class="text-xs text-gray-600 mt-1">
                    {{ appeal.recipientName }} | {{ appeal.bloodGroup }} | {{ appeal.bloodPints }} pint(s)
                  </p>
                  <p class="text-xs text-gray-600 mt-1">
                    Status: {{ appeal.status }}
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    By: {{ appeal.createdBy }} | {{ appeal.createdAtFormatted }}
                  </p>
                  </button>
                  <a
                    href="http://localhost:8081/blood-appeals"
                    @click.prevent="goToBloodAppeals(appeal)"
                    class="mt-2 inline-flex text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none"
                  >
                    Open blood appeals
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </transition>
      </div>

      <div class="relative ml-1">
        <button @click="dropdownOpen = !dropdownOpen"
          class="relative flex items-center w-18 h-12 overflow-hidden focus:outline-none">
          <img class="object-cover w-12 h-12 rounded-full" src="@/assets/images/avatar.png" alt="Your avatar" />
          <svg class="w-8 h-8 text-gray-800 dark:text-black ml-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
            width="20" height="20" fill="currentColor" viewBox="0 0 28 28">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m8 10 4 4 4-4" />
          </svg>
        </button>

        <div v-show="dropdownOpen" @click="dropdownOpen = false" class="fixed inset-0 z-10 w-full h-full"></div>

        <transition enter-active-class="transition duration-150 ease-out transform"
          enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150 ease-in transform" leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0">
          <div v-show="dropdownOpen" class="absolute right-0 z-20 w-48 py-2 mt-2 bg-white rounded-md shadow-xl">
            <button href="#"
              class="block px-4 py-2 text-sm w-full text-gray-700 hover:bg-custom-blue hover:text-white" @click="redirectProfilePage">Profile</button>
            <button class="block px-4 py-2 w-full text-sm text-gray-700 hover:bg-custom-blue hover:text-white"
              @click="handleLogout">
              Log out
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from 'vuex';
import apiClient from "@/services/api";

export default {
  name: "HeaderLayout",
  setup() {
    const dropdownOpen = ref(false);
    const notificationOpen = ref(false);
    const notificationsLoading = ref(false);
    const notificationError = ref("");
    const appealNotifications = ref([]);
    let pollingIntervalId = null;

    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const pageTitle = computed(() => {
      return route.meta.title || "Blood Management System";
    });

    const showBackArrow = computed(() => {
      return route.name === 'NewDonorRegistrationPage' || route.name === 'DonorDetailsPage' || route.name === 'EditDonorInformationPage'
        || route.name === 'NewRecordDonationForm' || route.name === 'NewDonorVitalsForm' || route.name === 'NewDonorDeferralForm' || route.name === 'NewReactionForm'
        || route.name === 'SupervisorDocumentsDetailsPage' || route.name === 'SupervisorAssessementDetailsPage';
    });

    const goBack = () => {
      router.go(-1); // Navigate back to the previous page
    };

    const handleLogout = () => {
      localStorage.removeItem('token');
      router.push("/");
    };

    const toggleBackground = () => {
      store.dispatch('auth/toggleBackgroundColor');
    };

    const mapStatus = (status) => {
      const statusMap = {
        pending_approval: "Pending Approval",
        appeal_ongoing: "Appeal Ongoing",
        rejected: "Rejected",
        available: "Available",
        pending_collection: "Pending Collection",
        collected: "Collected",
        approved: "Approved",
      };
      return statusMap[status] || status || "Unknown";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown date";
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "Unknown date";
      return date.toLocaleString();
    };

    const normalizeAppeal = (item) => ({
      id: item.blood_appeals_id || item.id,
      appealSerial: item.appeal_serial || "Unknown",
      recipientName: item.recipient_name || "Unknown recipient",
      bloodGroup: item.blood_group_name || "Unknown",
      bloodPints: item.blood_pints ?? 0,
      status: mapStatus(item.status),
      createdAtRaw: item.created_at,
      createdAtFormatted: formatDate(item.created_at),
      createdBy: item.user_name || "Unknown user",
      isUnread: item.viewed === false,
    });

    const fetchAppealNotifications = async () => {
      notificationsLoading.value = true;
      notificationError.value = "";
      try {
        const response = await apiClient.getFacilityAppeals();
        const { blood_appeals } = response.data || response;
        appealNotifications.value = (blood_appeals || [])
          .map(normalizeAppeal)
          .sort((a, b) => new Date(b.createdAtRaw) - new Date(a.createdAtRaw));
      } catch (error) {
        notificationError.value = "Failed to load notifications.";
        console.error("Failed to fetch facility appeals notifications:", error);
      } finally {
        notificationsLoading.value = false;
      }
    };

    const unreadCount = computed(() =>
      appealNotifications.value.filter((appeal) => appeal.isUnread).length
    );

    const markAppealAsRead = async (appeal) => {
      if (!appeal?.id || !appeal.isUnread) return;
      try {
        await apiClient.updateBloodAppeal(appeal.id, {
          blood_appeal: {
            viewed: true,
          },
        });
        appealNotifications.value = appealNotifications.value.map((item) =>
          item.id === appeal.id ? { ...item, isUnread: false } : item
        );
      } catch (error) {
        console.error("Failed to mark appeal as read:", error);
      }
    };

    const handleAppealClick = async (appeal) => {
      await markAppealAsRead(appeal);
    };

    const goToBloodAppeals = async (appeal) => {
      await markAppealAsRead(appeal);
      notificationOpen.value = false;
      router.push("/blood-appeals");
    };

    const markAllAsRead = async () => {
      const unreadAppeals = appealNotifications.value.filter((appeal) => appeal.isUnread);
      if (unreadAppeals.length === 0) return;
      try {
        await Promise.all(
          unreadAppeals.map((appeal) =>
            apiClient.updateBloodAppeal(appeal.id, {
              blood_appeal: {
                viewed: true,
              },
            })
          )
        );
        appealNotifications.value = appealNotifications.value.map((appeal) => ({
          ...appeal,
          isUnread: false,
        }));
      } catch (error) {
        console.error("Failed to mark all appeals as read:", error);
      }
    };

    const toggleNotificationPanel = () => {
      notificationOpen.value = !notificationOpen.value;
      if (notificationOpen.value) {
        fetchAppealNotifications();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchAppealNotifications();
      }
    };

    onMounted(() => {
      fetchAppealNotifications();
      pollingIntervalId = setInterval(fetchAppealNotifications, 60000);
      document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onUnmounted(() => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });

    return {
      pageTitle,
      showBackArrow,
      goBack,
      handleLogout,
      dropdownOpen,
      toggleBackground,
      notificationOpen,
      notificationsLoading,
      notificationError,
      appealNotifications,
      unreadCount,
      toggleNotificationPanel,
      handleAppealClick,
      markAllAsRead,
      goToBloodAppeals,
    };
  },
  mounted() {
    const token = localStorage.getItem('token');
    if (!token || this.isTokenExpired(token)) {
      this.$router.push({ name: 'Login' });
    }
  },
  methods: {
    isTokenExpired(token) {
      try {
        // Decode the token (assuming it's a JWT)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        // If token's expiration time is less than current time, it's expired
        return payload.exp && payload.exp < currentTime;
      } catch (error) {
        console.error('Failed to decode token:', error);
        return true; // Treat any decoding errors as an invalid token
      }
    },
    redirectProfilePage() {
      this.$router.push({ name: 'ProfilePage' });
    },
  }
};
</script>

<style lang="css" scoped>
/* Your existing styles */
</style>
